import React, { useMemo } from 'react';
import styled from 'styled-components';

import Alert from 'kingpin/Alert';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';
import LandingPage from 'components/LandingPage';
import Branding from 'components/Branding';
import formatMaintenanceEnd from 'screens/Settings/Advanced/MaintenanceMode/formatMaintenanceEnd';

const Content = styled.div`
  max-width: 560px;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
`;

// The app is only back once every active splash has ended, so we can only give
// an expected time if they all have one.
const getExpectedBackBy = (splashes: MaintenanceMode.Settings[]) => {
  const ends = splashes
    .map((s) => s.endsOn)
    .filter((endsOn): endsOn is string => endsOn !== null);
  if (ends.length === 0 || ends.length !== splashes.length) {
    return undefined;
  }

  return ends.reduce((latest, endsOn) =>
    new Date(endsOn) > new Date(latest) ? endsOn : latest,
  );
};

const MaintenanceSplashScreen = ({
  splashes,
  onStaffContinue,
}: {
  splashes: MaintenanceMode.Settings[];
  // Only provided for FleetOps staff
  onStaffContinue?: () => void;
}) => {
  const expectedBackBy = useMemo(() => getExpectedBackBy(splashes), [splashes]);

  return (
    <LandingPage data-testid="maintenance-splash-screen">
      <Branding />
      <Content>
        <Typography.Header type="H3">
          FleetOps is currently unavailable
        </Typography.Header>
        {splashes.map((s) => (
          <Typography.Body type="Body 12" key={s.id}>
            {s.message}
          </Typography.Body>
        ))}
        {expectedBackBy && (
          <Typography.Body type="Label">
            {`Expected back by ${formatMaintenanceEnd({
              endsOn: expectedBackBy,
            })}`}
          </Typography.Body>
        )}

        {onStaffContinue && (
          <div style={{ width: '100%', marginTop: 32, textAlign: 'left' }}>
            <Alert
              type="Warning"
              title="Only FleetOps staff can see this"
              body={
                <div>
                  <Typography.Body type="Body 12">
                    Customers are blocked by this splash screen. You can
                    continue into FleetOps, e.g. to turn maintenance mode off
                    from Settings → Advanced → UI.
                  </Typography.Body>
                  <Row style={{ marginTop: 12 }}>
                    <Button
                      type="Primary"
                      size="Small"
                      label="Continue to FleetOps"
                      onClick={onStaffContinue}
                      testId="maintenance-staff-continue"
                    />
                  </Row>
                </div>
              }
            />
          </div>
        )}
      </Content>
    </LandingPage>
  );
};

export default MaintenanceSplashScreen;
